import { ref, computed } from 'vue';
import { useMonacoTheme } from './useMonacoTheme';

/**
 * Shared composable for transform Monaco editor functionality
 * Reduces code duplication between PythonTransform, SqlTransform, and JavaScriptTransform
 *
 * @param {Object} props - Component props containing transform data
 * @param {Function} emit - Component emit function
 * @param {Object} options - Configuration options
 * @param {string} options.defaultTabId - Default active tab ID
 * @param {Object} options.templateMap - Map of tab IDs to template content
 */
export function useTransformEditor(props, emit, options = {}) {
    const { defaultTabId = 'COMMON_TRANSFORM', templateMap = {} } = options;

    // Theme management
    const { monacoTheme } = useMonacoTheme();

    // Editor state
    const activeTabId = ref(defaultTabId);

    // Computed properties
    const isSaving = computed(() => props.isSaving);

    // Helper function to update implementation field based on tabId
    const updateImplementationField = (implementation, tabId, content) => {
        switch (tabId) {
            case 'COMMON_TRANSFORM':
                implementation.common_transform = content;
                break;
            case 'VALUE_TRANSFORM':
                implementation.value_transform = content;
                break;
            case 'KEY_TRANSFORM':
                implementation.key_transform = content;
                break;
            case 'TOPIC_TRANSFORM':
                implementation.topic_transform = content;
                break;
            case 'VALUE_SCHEMA_TRANSFORM':
                implementation.value_schema_transform = content;
                break;
            case 'KEY_SCHEMA_TRANSFORM':
                implementation.key_schema_transform = content;
                break;
            // SQL-specific fields for Enrich
            case 'CREATE_TABLE_MAIN':
                if (!implementation.mainTable) implementation.mainTable = {};
                implementation.mainTable.createTableSQL = content;
                break;
            case 'CREATE_TABLE_LOOKUP':
                if (!implementation.lookupTable) implementation.lookupTable = {};
                implementation.lookupTable.createTableSQL = content;
                break;
            case 'ENRICH_SQL':
                implementation.enrichSQL = content;
                break;
            // SQL-specific fields for Join
            case 'JOIN_SQL':
                implementation.joinSQL = content;
                break;
            case 'CREATE_TABLE_1':
            case 'CREATE_TABLE_2':
            case 'CREATE_TABLE_3':
            case 'CREATE_TABLE_4':
            case 'CREATE_TABLE_5':
                const tableIndex = parseInt(tabId.split('_')[2]) - 1;
                if (!implementation.inputTables) implementation.inputTables = [];
                if (!implementation.inputTables[tableIndex]) implementation.inputTables[tableIndex] = {};
                implementation.inputTables[tableIndex].createTableSQL = content;
                break;
        }
    };

    // Tab management methods
    const handleTabSave = (saveData) => {
        // saveData contains: { activeTab, contents, modifiedTabs }
        // Update implementation with all tab contents
        if (saveData.contents) {
            const updatedImplementation = { ...props.implementationCurrentVersion.implementation };

            Object.entries(saveData.contents).forEach(([tabId, content]) => {
                updateImplementationField(updatedImplementation, tabId, content);
            });

            emit('implementation-updated', updatedImplementation);
        }
    };

    const handleContentChange = (tabId, content) => {
        const updatedImplementation = { ...props.implementationCurrentVersion.implementation };
        updateImplementationField(updatedImplementation, tabId, content);
        emit('implementation-updated', updatedImplementation);
    };

    const handleLoadTemplate = (tabId) => {
        const templateContent = templateMap[tabId];
        if (templateContent) {
            handleContentChange(tabId, templateContent);
            emit('show-example-clicked', { tabId, template: templateContent });
        }
    };

    const handleTabChange = (tabId) => {
        activeTabId.value = tabId;
    };

    const closeEditor = (firstTabId = defaultTabId) => {
        // Reset to first tab when closing
        activeTabId.value = firstTabId;
    };

    // Initialize active tab based on available tabs
    const initializeActiveTab = (tabs, fallbackTabId = defaultTabId) => {
        if (tabs && tabs.length > 0) {
            activeTabId.value = tabs[0].id || fallbackTabId;
        } else {
            activeTabId.value = fallbackTabId;
        }
    };

    return {
        // Theme
        monacoTheme,

        // State
        activeTabId,
        isSaving,

        // Methods
        handleTabSave,
        handleContentChange,
        handleLoadTemplate,
        handleTabChange,
        closeEditor,
        initializeActiveTab
    };
}
